import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Plus, Trash2, ImageUp, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import { CountrySelect } from "@/components/CountrySelect";

const EMPTY = {
  company: "", contact: "", phones: [""], emails: [""],
  country: "", address: "", website: "", notes: "",
  city_or_state: "", dealing_in: "", quality: "",
  rating: 0, sourced_from: "", supplier_since: "",
  sales_person_id: "", sales_person_name: "",
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[\d\s\-().]{7,20}$/;
const URL_RE = /^(https?:\/\/)?([\w-]+\.)+[\w]{2,}(\/\S*)?$/i;

function validateEmails(list) {
  return list.map((e) => {
    const v = e.trim();
    if (!v) return null;
    return EMAIL_RE.test(v) ? null : "Invalid email address";
  });
}

function validatePhones(list) {
  return list.map((p) => {
    const v = p.trim();
    if (!v) return null;
    return PHONE_RE.test(v) ? null : "Invalid mobile number";
  });
}

function validateWebsite(val) {
  const v = val.trim();
  if (!v) return null;
  return URL_RE.test(v) ? null : "Invalid website URL";
}

const TEXT_FIELDS_BEFORE_COUNTRY = [
  { key: "company", label: "Company", required: true, col: 2 },
  { key: "contact", label: "Contact Person", col: 1 },
];

const TEXT_FIELDS_AFTER_COUNTRY = [
  { key: "city_or_state", label: "City / State", col: 1 },
  { key: "website", label: "Website", col: 1 },
  { key: "sourced_from", label: "Sourced From", col: 1 },
  { key: "address", label: "Address", col: 2, textarea: true },
  { key: "dealing_in", label: "Dealing In", col: 2, textarea: true },
  { key: "quality", label: "Quality", col: 2, textarea: true },
  { key: "notes", label: "Internal Notes", col: 2, textarea: true },
];

function StarRating({ value, onChange }) {
  return (
    <div className="flex items-center gap-1" data-testid="supplier-input-rating">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(value === n ? 0 : n)}
          className="btn-ghost"
          style={{ padding: 2, color: n <= value ? "var(--primary)" : "var(--text-2)" }}
          title={`${n} star${n > 1 ? "s" : ""}`}
          data-testid={`supplier-star-${n}`}
        >
          <Star size={20} fill={n <= value ? "currentColor" : "none"} />
        </button>
      ))}
      {value > 0 && (
        <span style={{ fontSize: 12, color: "var(--text-2)", marginLeft: 4 }}>{value}/5</span>
      )}
    </div>
  );
}

export default function SupplierForm({ initial, onSave, onCancel, submitLabel = "Save Supplier" }) {
  const normalise = (c) => ({
    ...EMPTY,
    ...(c || {}),
    phones: (c?.phones?.length ? c.phones : [""]),
    emails: (c?.emails?.length ? c.emails : [""]),
  });
  const [form, setForm] = useState(() => normalise(initial));
  const [users, setUsers] = useState([]);
  const [extracting, setExtracting] = useState(false);
  const [errors, setErrors] = useState({ emails: [], phones: [], website: null });
  const fileRef = useRef(null);
  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (k === "website") setErrors((e) => ({ ...e, website: validateWebsite(v) }));
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  const setListItem = (key, idx, val) => {
    setForm((f) => {
      const next = f[key].map((x, j) => (j === idx ? val : x));
      if (key === "emails") setErrors((e) => ({ ...e, emails: validateEmails(next) }));
      if (key === "phones") setErrors((e) => ({ ...e, phones: validatePhones(next) }));
      return { ...f, [key]: next };
    });
  };
  const addListItem = (key) => setForm((f) => ({ ...f, [key]: [...f[key], ""] }));
  const removeListItem = (key, idx) => {
    setForm((f) => {
      const next = f[key].filter((_, j) => j !== idx);
      const safe = next.length ? next : [""];
      if (key === "emails") setErrors((e) => ({ ...e, emails: validateEmails(safe) }));
      if (key === "phones") setErrors((e) => ({ ...e, phones: validatePhones(safe) }));
      return { ...f, [key]: safe };
    });
  };

  const onSalesPerson = (id) => {
    const u = users.find((x) => x.id === id);
    setForm((f) => ({ ...f, sales_person_id: id, sales_person_name: u?.name || "" }));
  };

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Let the browser/axios set multipart Content-Type *with boundary* — don't set it manually.
      const { data } = await api.post("/suppliers/extract-image", fd);
      setForm((f) => ({
        ...f,
        company: data.company || f.company,
        contact: data.contact || f.contact,
        country: data.country || f.country,
        website: data.website || f.website,
        phones: (data.phones?.length ? data.phones : f.phones),
        emails: (data.emails?.length ? data.emails : f.emails),
      }));
      toast.success("Details extracted — review and save");
    } catch (err) {
      const msg = err?.response?.status === 503
        ? "AI extraction not configured (set ANTHROPIC_API_KEY)"
        : "Could not extract details from image";
      toast.error(msg);
    } finally {
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = () => {
    if (!form.company.trim()) return;
    const emailErrs = validateEmails(form.emails);
    const phoneErrs = validatePhones(form.phones);
    const websiteErr = validateWebsite(form.website);
    setErrors({ emails: emailErrs, phones: phoneErrs, website: websiteErr });
    if (emailErrs.some(Boolean) || phoneErrs.some(Boolean) || websiteErr) return;
    onSave({
      ...form,
      phones: form.phones.map((p) => p.trim()).filter(Boolean),
      emails: form.emails.map((p) => p.trim()).filter(Boolean),
    });
  };

  // Plain render function (not a component) so inputs keep focus across re-renders.
  const renderListField = (keyName, label, type) => {
    const fieldErrors = errors[keyName] || [];
    return (
      <div key={keyName} style={{ gridColumn: "1 / -1" }}>
        <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>{label}</label>
        {form[keyName].map((val, idx) => (
          <div key={idx} style={{ marginBottom: fieldErrors[idx] ? 2 : 6 }}>
            <div className="flex gap-2">
              <input
                type={type || "text"}
                value={val}
                onChange={(e) => setListItem(keyName, idx, e.target.value)}
                className="input-base"
                style={fieldErrors[idx] ? { borderColor: "var(--danger, #e53e3e)" } : undefined}
                data-testid={`supplier-input-${keyName}-${idx}`}
              />
              <button type="button" onClick={() => removeListItem(keyName, idx)} className="btn-ghost" style={{ padding: 6, color: "var(--danger)" }} title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
            {fieldErrors[idx] && (
              <div style={{ fontSize: 11, color: "var(--danger, #e53e3e)", marginTop: 2, marginBottom: 4 }}>
                {fieldErrors[idx]}
              </div>
            )}
          </div>
        ))}
        <button type="button" onClick={() => addListItem(keyName)} className="btn-ghost" style={{ padding: "4px 6px", color: "var(--primary)", fontSize: 12 }} data-testid={`supplier-add-${keyName}`}>
          <Plus size={12} /> Add {label.toLowerCase()}
        </button>
      </div>
    );
  };

  return (
    <div data-testid="supplier-form">
      <div className="flex justify-end mb-4">
        <input ref={fileRef} type="file" accept="image/*" onChange={onImage} style={{ display: "none" }} data-testid="supplier-image-input" />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={extracting} className="btn-outline" data-testid="supplier-extract-image">
          {extracting ? <Loader2 size={15} className="spin" /> : <ImageUp size={15} />}
          {extracting ? "Reading image…" : "Extract from image"}
        </button>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
        {TEXT_FIELDS_BEFORE_COUNTRY.map((f) => (
          <div key={f.key} style={{ gridColumn: f.col === 2 ? "1 / -1" : "auto" }}>
            <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>
              {f.label}{f.required ? <span style={{ color: "var(--primary)" }}> *</span> : null}
            </label>
            <input type={f.type || "text"} value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="input-base no-spin" data-testid={`supplier-input-${f.key}`} />
          </div>
        ))}

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Country</label>
          <CountrySelect value={form.country} onChange={(v) => set("country", v)} testId="supplier-input-country" />
        </div>

        {TEXT_FIELDS_AFTER_COUNTRY.map((f) => {
          const fieldErr = f.key === "website" ? errors.website : null;
          return (
            <div key={f.key} style={{ gridColumn: f.col === 2 ? "1 / -1" : "auto" }}>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>
                {f.label}{f.required ? <span style={{ color: "var(--primary)" }}> *</span> : null}
              </label>
              {f.textarea ? (
                <textarea value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} rows={2} className="input-base" style={{ resize: "vertical", fontFamily: "inherit" }} data-testid={`supplier-input-${f.key}`} />
              ) : (
                <input
                  type={f.type || "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => set(f.key, e.target.value)}
                  className="input-base no-spin"
                  style={fieldErr ? { borderColor: "var(--danger, #e53e3e)" } : undefined}
                  data-testid={`supplier-input-${f.key}`}
                />
              )}
              {fieldErr && (
                <div style={{ fontSize: 11, color: "var(--danger, #e53e3e)", marginTop: 2 }}>{fieldErr}</div>
              )}
            </div>
          );
        })}

        {renderListField("phones", "Phones")}
        {renderListField("emails", "Emails", "email")}

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Supplier Since</label>
          <input type="date" value={form.supplier_since || ""} onChange={(e) => set("supplier_since", e.target.value)} className="input-base" data-testid="supplier-input-supplier-since" />
        </div>

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Rating</label>
          <StarRating value={form.rating || 0} onChange={(v) => set("rating", v)} />
        </div>

        <div>
          <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Sales Person</label>
          <select value={form.sales_person_id} onChange={(e) => onSalesPerson(e.target.value)} className="input-base" data-testid="supplier-input-sales-person">
            <option value="">— Unassigned —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      <div className="flex gap-3 justify-end mt-6">
        <button onClick={onCancel} className="btn-outline" data-testid="supplier-form-cancel">Cancel</button>
        <button onClick={submit} className="btn-primary" data-testid="supplier-form-submit">{submitLabel}</button>
      </div>
    </div>
  );
}
